import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Agent } from "@/models/agent";
import { AgentDefinition, AgentExecutionResponse } from "@/models/agent-runtime";
import { fetchAgentData } from "@/services/agent-runtime-service/agent-data-fetcher";

export function useAgentRuntime(agentId: string | undefined) {
  const { user, loading: authLoading } = useAuth();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [agentSchema, setAgentSchema] = useState<AgentDefinition | null>(null);
  const [agentLoading, setAgentLoading] = useState(true);
  const [result, setResult] = useState<AgentExecutionResponse | null>(null);
  const [executionHistory, setExecutionHistory] = useState<AgentExecutionResponse[]>([]);

  // Refresh session on component mount to keep user logged in
  useEffect(() => {
    const refreshSession = async () => {
      try {
        const { error } = await supabase.auth.refreshSession();
        if (error) {
          console.error("Error refreshing session:", error);
        }
      } catch (error) {
        console.error("Session refresh error:", error);
      }
    };
    
    refreshSession();
  }, []);

  // Fetch agent data
  useEffect(() => {
    const loadAgentData = async () => {
      if (!agentId || !user) return;
      
      try {
        setAgentLoading(true);
        
        // Fetch agent data and handle the response
        const agentData = await fetchAgentData(agentId);
        
        if (agentData) {
          setAgent(agentData);
          // For this example, we'll mock the schema and history
          // In a real implementation, these would come from the backend
          setAgentSchema({} as AgentDefinition);
          setExecutionHistory([]);
        }
      } catch (error) {
        console.error("Failed to fetch agent data:", error);
      } finally {
        setAgentLoading(false);
      }
    };
    
    loadAgentData();
  }, [agentId, user]);

  return {
    user,
    loading: authLoading || agentLoading,
    agent,
    agentSchema,
    result,
    setResult,
    executionHistory
  };
}
